import type { TFunction } from "i18next"
import { CpuIcon, InfoIcon } from "lucide-react"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface ProcessorInfoPopoverProps {
  processorInfo?: TransactionsDetailsColumnsType["processorInformation"]
  t: TFunction
}

export const ProcessorInfoPopover = ({
  processorInfo,
  t,
}: ProcessorInfoPopoverProps) => {
  if (!processorInfo) {
    return <span className="text-muted-foreground">-</span>
  }

  const hasDetails =
    processorInfo.tid ||
    processorInfo.nsu ||
    processorInfo.authCode ||
    processorInfo.message ||
    processorInfo.returnCode

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm">
        {processorInfo.name || "-"}
      </span>
      {hasDetails && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
            >
              <CpuIcon size={14} />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-[calc(100vw-2rem)] max-w-80 p-0"
            align="end"
            side="bottom"
            sideOffset={4}
          >
            <div className="border-b bg-muted/50 px-4 py-3">
              <h4 className="flex items-center gap-2 text-sm font-semibold">
                <InfoIcon size={14} />
                {t(
                  "orders.transactionsTable.processorInfo.title"
                )}
              </h4>
            </div>
            <div className="space-y-3 p-4">
              {processorInfo?.name && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(
                      "orders.transactionsTable.processorInfo.processor"
                    )}
                  </span>
                  <span className="text-sm">
                    {processorInfo.name}
                  </span>
                </div>
              )}
              {processorInfo?.tid && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(
                      "orders.transactionsTable.processorInfo.tid"
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    <code className="break-all rounded bg-muted px-1.5 py-0.5 text-xs">
                      {processorInfo.tid}
                    </code>
                    <ClipboardButton
                      text={processorInfo.tid}
                      variant="ghost"
                      className="h-5 w-5 shrink-0"
                    />
                  </div>
                </div>
              )}
              {processorInfo?.nsu && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(
                      "orders.transactionsTable.processorInfo.nsu"
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    <code className="break-all rounded bg-muted px-1.5 py-0.5 text-xs">
                      {processorInfo.nsu}
                    </code>
                    <ClipboardButton
                      text={processorInfo.nsu}
                      variant="ghost"
                      className="h-5 w-5 shrink-0"
                    />
                  </div>
                </div>
              )}
              {processorInfo?.authCode && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(
                      "orders.transactionsTable.processorInfo.authCode"
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    <code className="rounded bg-muted px-1.5 py-0.5 text-xs">
                      {processorInfo.authCode}
                    </code>
                    <ClipboardButton
                      text={processorInfo.authCode}
                      variant="ghost"
                      className="h-5 w-5"
                    />
                  </div>
                </div>
              )}
              {processorInfo?.returnCode && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(
                      "orders.transactionsTable.processorInfo.returnCode"
                    )}
                  </span>
                  <Badge
                    variant="outline"
                    className="w-fit"
                  >
                    {processorInfo.returnCode}
                  </Badge>
                </div>
              )}
              {processorInfo.message && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-muted-foreground">
                    {t(
                      "orders.transactionsTable.processorInfo.message"
                    )}
                  </span>
                  <p className="rounded-md bg-muted/50 p-2 text-xs">
                    {processorInfo.message}
                  </p>
                </div>
              )}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  )
}
